/**
 * The code this deck shows, re-wrapped to fit a slide. Where each comes from:
 *
 * - CLI, part 04: labs/cloud/01, 03 and 04 of
 *   streamnative/dataplusagent-hackathon-tutorial. In the tutorial these are
 *   the calls the lab scripts make, not lines an attendee types.
 * - CLI, part 03 (file, memory store, skill, trigger): the CLI tabs of the
 *   docs pages build/files, build/memory, build/skills and run/triggers in
 *   orca-docs, with the names changed to the tutorial's example.
 * - Python and TypeScript: the turn loop of Orca 101
 *   (orca-website/src/lib/learn/codegen.ts), which is type-checked against
 *   runorca 0.3.0 and @runorca/orca-sdk 0.2.3; the client set-up of the
 *   tutorial's common.py and common.ts.
 * - JSON: agent/cloud/l3-live-context.json and l4-act.json of the tutorial.
 *   Two are not files you can open: AGENT_JSON is composed (see below), and
 *   MCP_JSON is two members of an agent, shown without the braces around them.
 *
 * Every flag of every `ork` line is read off that subcommand's `--help`
 * (ork 0.6.0). Change a sample only from its source, and re-run the checks in
 * README.md.
 */

const lines = (...l: string[]) => l.join('\n');

/* ── an agent is a definition ────────────────────────────────────────────
   Composed, not quoted: the agent Lab 1 creates, as a create body. The system
   prompt is agent/cloud/l1-hello.json's, cut short; the other fields are the
   ones an agent can carry, at their empty values, and the default harness
   written out. */

export const AGENT_JSON = lines(
  '{',
  '  "name": "hello-agent",',
  '  "model": "claude-sonnet-4-6",',
  '  "system": "You are the friendly guide ...",',
  '  "tools": [],',
  '  "mcp_servers": [],',
  '  "skills": [],',
  '  "metadata": { "harness": "claude_agent_sdk" }',
  '}',
);

/* ── a file, a memory store, a skill, a trigger ──────────────────────── */

export const FILE_CLI = lines(
  '# Upload once.',
  'ork agent files create --file ./runbook.pdf \\',
  '  --content-type application/pdf -o json',
  '',
  '# Attach it to a session.',
  'ork agent sessions resources add --session <session id> \\',
  '  --type file --file-id <file id> -o json',
);

export const MEMORY_CLI = lines(
  '# Create the store.',
  'ork agent memory-stores create --name takeover-notes \\',
  '  --description "What we learned about each account" -o json',
  '',
  '# Attach it, and let this session write to it.',
  'ork agent sessions resources add --session <session id> \\',
  '  --type memory-store --memory-store-id <store id> \\',
  '  --access read_write',
);

export const SKILL_CLI = lines(
  '# Upload the bundle: a folder with a SKILL.md in it.',
  'ork agent skills create --display-title takeover-playbook \\',
  '  --file playbook/SKILL.md --file playbook/signals.md -o json',
  '',
  '# List it on the agent. The update is a new agent version.',
  'ork agent update <agent id> --skill <skill id> -o json',
);

/* The vault is on the trigger because its sessions get only what it names: a
   session started without the vault calls the MCP server with no credential. */
export const TRIGGER_CLI = lines(
  '# Each night at 02:00 UTC, a new session gets the payload.',
  'ork agent triggers create --name nightly-review \\',
  '  --agent <agent id> --source-type cron \\',
  '  --schedule "0 2 * * *" --timezone Etc/UTC \\',
  '  --session-mode SESSION_PER_EVENT \\',
  '  --payload "Review the login failures of the last day." \\',
  '  --environment-id <environment id> --vault-id <vault id> \\',
  '  -o json',
);

/* ── what you type in the tutorial ───────────────────────────────────────
   The "In your path's folder" tables of labs/cloud/01, 03 and 04. Labs 0 and 2
   have no script: one is set-up, the other is SQL in the console. */

export const RUN_CLI = lines(
  './l1_hello.sh          # Lab 1',
  './l3_live_context.sh   # Lab 3',
  './l4_act.sh            # Lab 4',
);

export const RUN_PYTHON = lines(
  'python l1_hello.py          # Lab 1',
  'python l3_live_context.py   # Lab 3',
  'python l4_act.py            # Lab 4',
);

export const RUN_TS = lines(
  'npm run l1   # Lab 1',
  'npm run l3   # Lab 3',
  'npm run l4   # Lab 4',
);

/* ── three ways in ───────────────────────────────────────────────────── */

export const CONNECT_CLI = lines(
  'brew install orca-ae/tap/ork',
  '',
  'export ORCA_REGISTRY_URL=$ORCA_BASE_URL',
  'export ORCA_ACCESS_TOKEN=$SN_API_KEY',
  '',
  'ork agent list',
);

export const CONNECT_PYTHON = lines(
  '# pip install runorca',
  'from orca import Orca',
  '',
  'client = Orca(',
  '    base_url=ORCA_BASE_URL,',
  '    api_key=SN_API_KEY,',
  ')',
);

export const CONNECT_TS = lines(
  '// npm install @runorca/orca-sdk',
  'import Orca from "@runorca/orca-sdk";',
  '',
  'const orca = new Orca({',
  '  baseURL: ORCA_BASE_URL,',
  '  apiKey: SN_API_KEY,',
  '});',
);

/* ── four calls (Lab 1) ──────────────────────────────────────────────────
   The stream starts at cursor 0, as the lab's script does (cli/lib.sh). The
   lab's page shows the command without it; opened with no cursor a stream
   starts from now, and a short turn can end before it attaches. The last
   comment says so, which is the one place the comments are not the lab's. */

export const FOUR_CALLS_CLI = lines(
  "# 1. An environment: where your agent's sessions run.",
  'ork agent environments create --name hello-env-ana -o json',
  '',
  '# 2. An agent: a model plus a system prompt.',
  'ork agent create --name hello-agent-ana --model "$ORCA_MODEL" \\',
  '  --system "$(jq -r .system ../agent/cloud/l1-hello.json)" -o json',
  '',
  '# 3. A session: one conversation, pinned to this exact agent version.',
  'ork agent sessions create --agent <agent id> --agent-version 1 \\',
  '  --environment-id <environment id> --title "L1: hello" -o json',
  '',
  '# 4. Send a message, then stream the reply from the start of the log.',
  'ork agent sessions events send message --session <session id> --text "Hi! ..."',
  'ork agent sessions events stream --session <session id> --from-cursor 0 --timeout 15s',
);

/* ── the turn loop ───────────────────────────────────────────────────────
   Correct for a session's first turn only: from cursor 0 the stream replays
   every earlier turn, and this loop stops at the first idle it meets. */

export const TURN_PYTHON = lines(
  'client.sessions.events.send(',
  '    session.id,',
  '    events=[{',
  '        "type": "user.message",',
  '        "content": [{"type": "text", "text": question}],',
  '    }],',
  ')',
  '',
  'stream = client.sessions.events.stream(',
  '    session.id, from_cursor="0",',
  ')',
  'for event in stream:',
  '    print(event.type)',
  '    if event.type == "session.status_idle":',
  '        break',
);

export const TURN_TS = lines(
  'await orca.sessions.events.send(session.id, {',
  '  events: [{',
  '    type: "user.message",',
  '    content: [{ type: "text", text: question }],',
  '  }],',
  '});',
  '',
  'const stream = await orca.sessions.events.stream(',
  '  session.id, { from_cursor: "0" },',
  ');',
  'for await (const event of stream) {',
  '  console.log(event.type);',
  '  if (event.type === "session.status_idle") break;',
  '}',
);

/* ── an MCP server and an allow-list (Lab 3) ─────────────────────────── */

export const MCP_JSON = lines(
  '"mcp_servers": [',
  '  { "name": "streamnative", "type": "url", "url": "${SN_MCP_URL}" }',
  '],',
  '"tools": [',
  '  {',
  '    "type": "mcp_toolset",',
  '    "mcp_server_name": "streamnative",',
  '    "default_config": { "enabled": false },',
  '    "configs": [',
  '      { "name": "sql_workspace_list_databases", "enabled": true,',
  '        "permission_policy": { "type": "always_allow" } },',
  '      { "name": "sql_workspace_query", "enabled": true,',
  '        "permission_policy": { "type": "always_allow" } }',
  '    ]',
  '  }',
  ']',
);

/* ── the credential goes in a vault (Lab 3) ──────────────────────────── */

export const VAULT_CLI = lines(
  'ork agent vaults create --display-name hello-vault-ana -o json',
  '',
  'ork agent vaults credentials create --vault <vault id> \\',
  '  --display-name streamnative-mcp \\',
  '  --mcp-server-url "$SN_MCP_URL" \\',
  '  --oauth-scope "$SN_MCP_OAUTH_SCOPE" -o json',
  '',
  'ork agent sessions create --agent <agent id> --agent-version 2 \\',
  '  --environment-id <environment id> --vault-id <vault id> \\',
  '  --title "L3: live context" -o json',
);

/* ── ask before it acts (Lab 4) ──────────────────────────────────────── */

export const ASK_JSON = lines(
  '{ "name": "sql_workspace_insert_rows", "enabled": true,',
  '  "permission_policy": { "type": "always_ask" } }',
);

export const CONFIRM_CLI = lines(
  'ork agent sessions events send tool-confirmation \\',
  '  --session <session id> \\',
  '  --tool-use-id <tool use event id> \\',
  '  --decision allow',
);
